import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Clock, DollarSign, Target, TrendingUp, CheckCircle, Zap, Users, Settings } from "lucide-react";

const GermanyPage = () => {
  const painPoints = [
    {
      icon: <DollarSign className="h-8 w-8 text-orange-500" />,
      title: "Verschwenderisches Google Ads Budget",
      description: "Sie zahlen für Keywords, die keine Conversions bringen. Viele deutsche Unternehmen verlieren 40-60% ihres Budgets an ineffiziente Kampagnen."
    },
    {
      icon: <Target className="h-8 w-8 text-orange-500" />,
      title: "Falsche Keyword-Strategie",
      description: "Deutsche Keywords haben spezifische Anforderungen. Generische Targeting-Ansätze funktionieren nicht für den lokalen B2B-Markt."
    },
    {
      icon: <Clock className="h-8 w-8 text-orange-500" />,
      title: "Zu viel Zeit für Setup",
      description: "Google Ads ist komplex. Viele Agenturen brauchen Wochen für Setup, während Sie auf Ergebnisse warten."
    }
  ];

  const features = [
    {
      icon: <Zap className="h-8 w-8 text-blue-500" />,
      title: "Budget-Intelligente Kampagnen",
      description: "AI-gesteuerte Keyword-Filterung für deutsche Märkte. Bezahlen Sie nur für hochqualifizierte Anfragen."
    },
    {
      icon: <Target className="h-8 w-8 text-blue-500" />,
      title: "Lokale Keyword-Analyse",
      description: "Echte Google Ads Gebote für deutsche Keywords. Verstehen Sie exakt, was jedes Keyword kostet und bringt."
    },
    {
      icon: <TrendingUp className="h-8 w-8 text-blue-500" />,
      title: "Automatische Optimierung",
      description: "Kampagnen passen sich täglich an. Bessere Conversion Rates mit weniger manuellem Aufwand."
    },
    {
      icon: <Users className="h-8 w-8 text-blue-500" />,
      title: "Deutsche Support-Sprache",
      description: "Vollständiger Support auf Deutsch. Keine Sprachbarrieren bei technischen Fragen."
    }
  ];

  const steps = [
    {
      number: "1",
      title: "Erzählen Sie uns von Ihrem Geschäft",
      description: "Teilen Sie Ihre Geschäftsdetails, Zielkunden und Budget mit. Nur 2 Minuten Setup."
    },
    {
      number: "2",
      title: "AI erstellt Ihre Kampagnen",
      description: "Upfixe analysiert Ihren Website-Inhalt und erstellt professionelle, auf Deutsch optimierte Kampagnen."
    },
    {
      number: "3",
      title: "Mehr Kunden mit weniger Budgetverschwastung",
      description: "Sehen Sie hochqualifizierte Anfragen. Budget-Intelligenz bedeutet schneller Erfolg."
    }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>Google Ads für deutsche Unternehmen | Budget-Smart Automation | Upfixe</title>
        <meta name="description" content="Budget-intelligente Google Ads für deutsche Unternehmen. Intelligente Keyword-Filterung, lokale Optimierung, deutscher Support. Jetzt kostenlos starten." />
        <meta name="keywords" content="Google Ads Deutschland, Google Ads Agentur, Keywords Optimierung, Budget Optimierung, deutsche Kampagnen" />
        <meta property="og:title" content="Google Ads für deutsche Unternehmen | Budget-Smart Automation | Upfixe" />
        <meta property="og:description" content="Budget-intelligente Google Ads für deutsche Unternehmen. Intelligente Keyword-Filterung, lokale Optimierung, deutscher Support." />
        <link rel="canonical" href="https://upfixe.com/de" />
        <link rel="alternate" hrefLang="x-default" href="https://upfixe.com/" />
        <link rel="alternate" hrefLang="en" href="https://upfixe.com/" />
        <link rel="alternate" hrefLang="de" href="https://upfixe.com/de" />
        <link rel="alternate" hrefLang="fr" href="https://upfixe.com/fr" />
        <link rel="alternate" hrefLang="en-GB" href="https://upfixe.com/uk" />
        <link rel="alternate" hrefLang="en-AU" href="https://upfixe.com/au" />
      </Helmet>
      <Header />

      <main className="flex-grow pt-24 pb-16">
        <div className="container mx-auto px-4 md:px-6">
          {/* Hero Section */}
          <div className="text-center mb-20">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              Stoppen Sie, Budget auf <br />
              <span className="text-blue-600">ineffiziente Keywords zu verschwenden.</span>
            </h1>
            <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              Google Ads für deutsche Unternehmen, die wirklich funktionieren. AI-gesteuerte Budget-Intelligenz. Keine teuren Agenturen. Keine Spielereien.
            </p>
            <div className="flex justify-center mb-6">
              <Button
                size="lg"
                className="text-lg px-8 h-14 bg-blue-600 hover:bg-blue-700 font-semibold"
                asChild
              >
                <Link to="/auth?signup=true">Kostenlos Starten</Link>
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">Keine Kreditkarte erforderlich • Deutsche Support • Professionelle Ergebnisse</p>
          </div>

          {/* Problem Section */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Deutsche Unternehmen: Warum Google Ads schwierig ist</h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Deutsche Keywords sind teuer. Generische Targeting-Tools funktionieren nicht für unseren B2B-Markt.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {painPoints.map((point, index) => (
                <Card key={index} className="text-center p-6 border-orange-200 bg-orange-50/50">
                  <div className="flex justify-center mb-4">{point.icon}</div>
                  <h3 className="text-xl font-semibold mb-3">{point.title}</h3>
                  <p className="text-muted-foreground">{point.description}</p>
                </Card>
              ))}
            </div>
          </div>

          {/* Solution Section */}
          <div className="mb-20 bg-blue-50 p-12 rounded-2xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-6">Upfixe: Budget-Intelligenz für Deutschland</h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Echte Google Ads Gebote. Deutsche Keyword-Intelligenz. Automatische Budget-Optimierung, die funktioniert.
              </p>
            </div>
          </div>

          {/* Features Section */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Warum deutsche Agenturen Upfixe wählen</h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Intelligente Tools für deutsche Märkte. Keine Komplexität. Nur Ergebnisse.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {features.map((feature, index) => (
                <Card key={index} className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                      {feature.icon}
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                      <p className="text-muted-foreground">{feature.description}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>

          {/* How It Works */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">In 3 Schritten zum Erfolg</h2>
              <p className="text-xl text-muted-foreground">
                Von Setup bis zu qualifizierten Anfragen in weniger als 30 Minuten.
              </p>
            </div>

            <div className="max-w-4xl mx-auto">
              {steps.map((step, index) => (
                <div key={index} className={`flex flex-col md:flex-row gap-6 items-center mb-12 ${index === steps.length - 1 ? 'mb-0' : ''}`}>
                  <div className="flex-shrink-0 w-16 h-16 rounded-full bg-blue-600 flex items-center justify-center text-white text-2xl font-bold">
                    {step.number}
                  </div>
                  <div className="text-center md:text-left">
                    <h3 className="text-2xl font-semibold mb-3">{step.title}</h3>
                    <p className="text-lg text-muted-foreground">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Final CTA */}
          <div className="text-center bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-12 rounded-2xl">
            <h2 className="text-4xl font-bold mb-4">Bereit für intelligente Google Ads?</h2>
            <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
              Tausende deutsche Unternehmer automatisieren bereits ihre Google Ads. Keine Agenturen. Keine Komplexität. Nur Ergebnisse.
            </p>
            <div className="flex justify-center mb-6">
              <Button
                size="lg"
                className="text-lg px-8 h-14 bg-white text-blue-600 hover:bg-gray-100 font-semibold"
                asChild
              >
                <Link to="/auth?signup=true">Kostenlos Testen</Link>
              </Button>
            </div>
            <p className="text-sm opacity-80">Einfaches Setup • Professionelle Kampagnen • Deutsche Unterstützung</p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default GermanyPage;
